/**
 * HESAP SEÇİCİ — Varlık Dökümü ve Günlük Analiz (back-test, 14.10.2026).
 *
 * Tek seçim: "Bütün defter (gerçek hesaplar)", gerçek hesaplar, sanal
 * hesaplar (ayrı grup). Çoklu seçim (Günlük Analiz): işaretlenen hesaplar
 * ayrı çizgiler olarak karşılaştırılır; hiçbiri işaretli değilse bütün defter.
 */

import { useState } from "react";
import { BUTUN_DEFTER, type KapsamSecenegi } from "../lib/hesapKapsami";
import { Field, cx } from "../ui/primitives";
import { M } from "../dil";

export const KARSILASTIRMA_MAX = 5;

export function HesapSecici({
  id,
  secenekler,
  deger,
  onChange,
  className,
}: {
  readonly id: string;
  readonly secenekler: readonly KapsamSecenegi[];
  readonly deger: string;
  readonly onChange: (id: string) => void;
  readonly className?: string;
}) {
  const gercek = secenekler.filter((s) => !s.sanal);
  const sanal = secenekler.filter((s) => s.sanal);
  // Seçili kapsam listede yoksa (silinmiş sanal hesap) bütün deftere düşer.
  const gecerli = deger === BUTUN_DEFTER || secenekler.some((s) => s.id === deger) ? deger : BUTUN_DEFTER;
  return (
    <Field label={M.hesaplar.secici.hesap} htmlFor={id} className={className ?? "w-56"}>
      <select id={id} className="pf-input" value={gecerli} onChange={(e) => onChange(e.currentTarget.value)}>
        <option value={BUTUN_DEFTER}>{M.hesaplar.secici.butunKasaGercek}</option>
        {gercek.length > 0 && (
          <optgroup label={M.hesaplar.secici.hesaplar}>
            {gercek.map((s) => (
              <option key={s.id} value={s.id}>
                {s.ad}
              </option>
            ))}
          </optgroup>
        )}
        {sanal.length > 0 && (
          <optgroup label={M.hesaplar.secici.sanalHesaplar}>
            {sanal.map((s) => (
              <option key={s.id} value={s.id}>
                {M.hesaplar.secici.sanalEk(s.ad)}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </Field>
  );
}

/** Çoklu seçim: açılır kutuda işaret kutuları. */
export function HesapCokluSecici({
  secenekler,
  secili,
  onChange,
  enCok = KARSILASTIRMA_MAX,
}: {
  readonly secenekler: readonly KapsamSecenegi[];
  readonly secili: readonly string[];
  readonly onChange: (ids: readonly string[]) => void;
  /** En çok kaç hesap karşılaştırılır (gelir modeli: ücretsizde 2). */
  readonly enCok?: number;
}) {
  const [acik, setAcik] = useState(false);
  const gecerli = secili.filter((id) => secenekler.some((s) => s.id === id));
  const ozet =
    gecerli.length === 0
      ? M.hesaplar.secici.butunKasa
      : gecerli.length === 1
        ? (secenekler.find((s) => s.id === gecerli[0])?.ad ?? M.hesaplar.secici.birHesap)
        : M.hesaplar.secici.karsilastiriliyor(gecerli.length);
  const degistir = (id: string) => {
    if (gecerli.includes(id)) onChange(gecerli.filter((x) => x !== id));
    else if (gecerli.length < enCok) onChange([...gecerli, id]);
  };
  const grup = (ad: string, liste: readonly KapsamSecenegi[]) =>
    liste.length === 0 ? null : (
      <fieldset className="space-y-1">
        <legend className="text-[11px] font-semibold uppercase tracking-wider text-muted">{ad}</legend>
        {liste.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={gecerli.includes(s.id)}
              disabled={!gecerli.includes(s.id) && gecerli.length >= enCok}
              onChange={() => degistir(s.id)}
            />
            {s.renk !== undefined && (
              <span aria-hidden="true" className="inline-block h-3 w-1 rounded" style={{ background: `var(--pf-hesap-${s.renk})` }} />
            )}
            {s.ad}
            {s.sanal && <span className="text-faint">{M.hesaplar.secici.sanal}</span>}
          </label>
        ))}
      </fieldset>
    );
  return (
    <div className="relative flex min-w-0 flex-col gap-1">
      <span className="text-[12px] font-semibold text-muted">{M.hesaplar.secici.hesaplar}</span>
      <button
        type="button"
        aria-expanded={acik}
        aria-haspopup="true"
        onClick={() => setAcik((a) => !a)}
        className={cx("pf-input flex items-center justify-between gap-2 text-left", "sm:w-56")}
      >
        <span className="truncate">{ozet}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {acik && (
        <div className="absolute top-full z-20 mt-1 w-72 space-y-3 rounded-[var(--pf-radius-sm)] border border-line bg-surface p-3 shadow-lg">
          <p className="text-[12px] text-muted">
            {M.hesaplar.secici.aciklama(enCok)}
          </p>
          {grup(M.hesaplar.secici.hesaplar, secenekler.filter((s) => !s.sanal))}
          {grup(M.hesaplar.secici.sanalHesaplar, secenekler.filter((s) => s.sanal))}
          <div className="flex justify-between">
            <button type="button" className="text-[12px] text-accent" onClick={() => onChange([])}>
              {M.hesaplar.secici.butunKasa}
            </button>
            <button type="button" className="text-[12px] text-accent" onClick={() => setAcik(false)}>
              {M.hesaplar.secici.kapat}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
